<script lang="ts" setup>
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  onUpdated,
  ref,
} from "vue";

import StepperV2 from "~/components/stepper/StepperV2.vue";
import { useBroadcast } from "~/stores/broadcast.store";
import { useContacts } from "~/stores/contacts.store";
import { useRouter } from "vue-router";
import { useToast } from "@profeat/ui-kit";
import { useI18n } from "vue-i18n";
import { useChannels } from "~/stores/channel.store";
import { storeToRefs } from "pinia";
import { ChannelState } from "~/entities/channel";
import BroadcastSenderStepContacts from "./BroadcastSenderSteps/BroadcastSenderStepContacts.vue";
import BroadcastV2SendingConfirm from "./modal/BroadcastV2SendingConfirm.vue";
import BroadcastSenderStepsTemplate from "./BroadcastSenderSteps/BroadcastSenderStepsTemplate.vue";
import BroadcastSenderStepSend from "./BroadcastSenderSteps/BroadcastSenderStepSend.vue";

const broadcastStore = useBroadcast();
const contactsStore = useContacts();
const channelsStore = useChannels();

const router = useRouter();
const { showToast } = useToast();
const { t } = useI18n();

enum BroadcastV2Step {
  Contact = 1,
  Template = 2,
  Send = 3,
}

const step = ref(BroadcastV2Step.Contact);
const isMounted = ref(false);

const updateStep = (payload: BroadcastV2Step) => {
  step.value = payload;

  if (payload === BroadcastV2Step.Template && !hasBeenUpdated.value) {
    hasBeenUpdated.value = true;
  }
};

/**
 * Contacts
 */
const isValidContacts = computed(() => {
  return !!contactsStore.getSelectionsCount;
});
const hasBeenUpdated = ref(false);

/**
 * Modal
 */
const modalConfirmation = ref<InstanceType<typeof BroadcastV2SendingConfirm>>();

/**
 * Send
 */
const isValidSend = computed(() => {
  return (
    !broadcastStore.isFetching &&
    !!contactsStore.getSelectionsCount &&
    channelsStore.selectedWabaChannel?.state === ChannelState.active
  );
});

const isChannelValid = computed<boolean>(() => {
  const channel = channelsStore.selectedWabaChannel;
  return channel !== null && channel.state === ChannelState.active;
});

const isValidTemplate = computed(() => {
  return !broadcastStore.hasErrors && isChannelValid.value == true;
});

const sendBroadcast = async ({ fake }: { fake?: boolean }) => {
  if (broadcastStore.isFetching) return;

  broadcastStore.setFetching(true);

  try {
    await broadcastStore.create({ fake });
    router.push({ name: "broadcast" });
  } catch (err: any) {
    showToast({ color: "error", text: t("errors.oops") });
  }

  broadcastStore.setFetching(false);
};

onBeforeMount(() => {
  if (!channelsStore.selectedWabaChannel) {
    router.push({ name: "broadcast" });
  }
});

onMounted(async () => {
  await contactsStore.reloadContacts(true);
  const toTemplate = router.currentRoute.value.query.toTemplate;
  if (toTemplate) {
    step.value = BroadcastV2Step.Template;
    hasBeenUpdated.value = true;
  }
  isMounted.value = true;
});
onBeforeUnmount(() => {
  broadcastStore.$reset();
  contactsStore.$reset();
});
</script>

<template>
  <div v-if="isMounted" class="broadcast-wrapper">
    <BroadcastV2SendingConfirm ref="modalConfirmation" />

    <StepperV2
      v-model:step="step"
      @update:step="updateStep"
      @finalize="sendBroadcast({ fake: false })"
      @fake-finalize="sendBroadcast({ fake: true })"
      :tabs="[
        { title: $t('broadcast.stepper.contacts'), isValid: isValidContacts },
        { title: $t('broadcast.stepper.template'), isValid: isValidTemplate },
        {
          title: $t('broadcast.stepper.send'),
          isValid: isValidSend,
          isLoading: broadcastStore.isFetching,
        },
      ]"
      :wrapper-class="['broadcast-stepper']"
      :content-class="['broadcast-stepper-content']"
      :done-text="
        broadcastStore.delay === null
          ? $t('broadcast.delay.sendNoDelay')
          : $t('broadcast.delay.sendWithDelay')
      "
    >
      <template #1>
        <div class="broadcast-sender-count">
          {{ contactsStore.getSelectionsCount }} из
          {{ contactsStore.getCount.all }}
        </div>
        <BroadcastSenderStepContacts :has-been-updated="hasBeenUpdated" />
      </template>
      <template #2>
        <BroadcastSenderStepsTemplate />
      </template>
      <template #3>
        <BroadcastSenderStepSend />
      </template>
    </StepperV2>
  </div>
</template>

<style lang="scss" scoped>
.loader-wrapper {
  width: 100%;
  height: 100%;
  position: relative;

  .preloader {
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
}

.broadcast-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  box-sizing: border-box;
  padding: 38px 0px 0px;

  .broadcast-customization {
    display: flex;
    gap: 40px;
    padding: 40px;
    overflow: hidden;

    .content-settings {
      width: 65%;
      display: flex;
      flex-direction: column;
      gap: 32px;
      overflow-y: auto;
      max-height: 100%;

      &::-webkit-scrollbar {
        width: 0;
      }
    }

    .content {
      width: 35%;
      display: flex;
      flex-direction: column;
      gap: 24px;
      overflow-y: auto;
      max-height: 100%;

      &::-webkit-scrollbar {
        width: 0;
      }
    }
  }

  .broadcast-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    box-sizing: border-box;
    height: 76px;
    padding: 16px 40px;
    border-top: 1px solid var(--common-rain);

    .action {
      width: 194px;
    }
  }
}
</style>

<style scoped>
.broadcast-stepper {
  position: relative;
}

.broadcast-sender-count {
  position: absolute;
  top: 30px;
  left: 84px;
  box-sizing: border-box;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: var(--common-asphalt);
  z-index: 1000;
}
</style>
